/**
 * AutomationPolicies — Per-repo autonomy tier + shadow-mode dial
 *
 * Customers configure how aggressively CARTIE may act on their repos.
 * Defaults are SAFE (T3 = draft PR + shadow ON for 14 days).
 *
 * Every tier change + shadow graduation is audit-logged.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Shield, ShieldAlert, ShieldCheck, ShieldQuestion, Plus, Trash2, Edit3,
  Eye, EyeOff, Lock, Sparkles, AlertTriangle, X, Save,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  T1: { icon: ShieldAlert,    color: 'rose',    label: 'Auto-merge',         desc: 'CARTIE merges after CI + 24h soak. Opt-in only.', danger: 'high' },
  T2: { icon: Shield,         color: 'amber',   label: 'Auto-PR + human merge', desc: 'CARTIE opens the PR. Your engineer reviews + merges.', danger: 'medium' },
  T3: { icon: ShieldCheck,    color: 'emerald', label: 'Draft PR + Slack',    desc: 'Safe default. PR opens as DRAFT — your engineer un-drafts and merges.', danger: 'low' },
  T4: { icon: ShieldQuestion, color: 'slate',   label: 'Detect only',         desc: 'CARTIE flags the issue. Your engineer writes the fix.', danger: 'none' },
};

export default function AutomationPolicies() {
  const [policies, setPolicies] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // policy object or "new"
  const [savingId, setSavingId] = useState(null);
  // v35.09 — GitHub connection state
  const [ghConnection, setGhConnection] = useState(null);
  const [ghJustConnected, setGhJustConnected] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const loadGhConnection = async () => {
    try {
      const r = await axios.get(`${API}/github/me/connection`, { headers: headers() });
      setGhConnection(r.data);
    } catch (_) { setGhConnection({ connected: false }); }
  };

  const loadMe = async () => {
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: headers() });
      setCurrentUserId(r.data?.id || r.data?._id);
    } catch (_) {/* noop */}
  };

  const connectGithub = () => {
    if (!currentUserId) {
      window.alert('Please refresh and try again — could not identify your user.');
      return;
    }
    // Backend embeds the user_id into the OAuth `state` so the callback can
    // persist the token under our user. After OAuth, GitHub redirects back to
    // /api/github/callback → which redirects to /policies/automation?github_connected=1.
    const url = `${API}/github/login?user_id=${encodeURIComponent(currentUserId)}`;
    window.location.href = url;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        axios.get(`${API}/policies/automation`, { headers: headers() }),
        axios.get(`${API}/policies/automation/tiers`),
      ]);
      setPolicies(p.data?.items || []);
      setTiers(t.data?.tiers || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    loadGhConnection();
    loadMe();
    // Pick up `?github_connected=1` from the OAuth callback redirect
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('github_connected') === '1') {
        setGhJustConnected(true);
        url.searchParams.delete('github_connected');
        url.searchParams.delete('github_user');
        window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
        // Refresh connection state after a brief moment
        setTimeout(() => loadGhConnection(), 250);
      }
    } catch (_) {/* noop */}
  }, []);

  const save = async (form) => {
    setSavingId(form.id || 'new');
    try {
      if (form.id) {
        await axios.patch(`${API}/policies/automation/${form.id}`, form, { headers: headers() });
      } else {
        await axios.post(`${API}/policies/automation`, form, { headers: headers() });
      }
      setEditing(null);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      alert(msg);
    } finally {
      setSavingId(null);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this policy? CARTIE will fall back to default safe behavior for this repo.')) return;
    setSavingId(id);
    try {
      await axios.delete(`${API}/policies/automation/${id}`, { headers: headers() });
      await load();
    } finally { setSavingId(null); }
  };

  const graduate = async (id) => {
    if (!window.confirm("Graduate out of shadow mode? CARTIE will START opening real PRs at the configured tier on this repo.")) return;
    setSavingId(id);
    try {
      await axios.post(`${API}/policies/automation/${id}/graduate-shadow`, {}, { headers: headers() });
      await load();
    } finally { setSavingId(null); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>Automation Policies · CARTIE</title>
      </Helmet>

      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur">
        <Link to="/dashboard" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="policies-back">
          ← Dashboard
        </Link>
        <div className="text-xs font-mono text-slate-400">/api/policies/automation</div>
      </header>

      <section className="px-4 py-10 max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> Tiered autonomy · Shadow mode
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight" data-testid="policies-h1">
              Your repos, your tier.
            </h1>
            <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
              Decide per-repo how aggressively CARTIE may act. Default is <strong className="text-emerald-300">T3 (draft PR + Slack) + 14-day shadow mode</strong>.
              Graduate out of shadow once you trust our judgment.{' '}
              <Link to="/how-it-works" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2" data-testid="policies-link-how-it-works">
                See how CARTIE works with your team
              </Link>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing({ id: null, tier: 'T3', shadow_mode_enabled: true, excluded_paths: [], notes: '' })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-all hover:-translate-y-0.5"
            data-testid="add-policy-btn"
          >
            <Plus className="w-4 h-4" /> Add policy
          </button>
        </div>

        {/* v35.09 — Connect GitHub panel (closes the loop: detect → PR) */}
        <div className="mt-6" data-testid="github-connect-panel">
          {ghJustConnected && ghConnection?.connected && (
            <div className="mb-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200 flex items-center gap-2" data-testid="gh-just-connected-toast">
              <ShieldCheck className="w-4 h-4" />
              <span>Connected to GitHub. CARTIE can now open PRs at the tier you set per repo.</span>
            </div>
          )}
          {ghConnection?.connected ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3 flex-wrap" data-testid="gh-connected-panel">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest text-emerald-300">GitHub connected</div>
                <div className="text-sm font-bold text-white mt-0.5" data-testid="gh-login">@{ghConnection.login}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  Scopes: <code className="text-emerald-300">{ghConnection.scopes || 'repo, user'}</code>
                  {ghConnection.token_encrypted ? ' · token encrypted at rest' : ' · token stored plaintext (audit)'}
                </div>
              </div>
              <button
                type="button"
                onClick={connectGithub}
                className="text-[11px] font-mono text-slate-400 hover:text-emerald-300 transition-colors"
                data-testid="gh-reconnect-btn"
              >
                Reconnect
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3 flex-wrap" data-testid="gh-disconnected-panel">
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest text-amber-300">Not connected</div>
                <div className="text-sm font-bold text-white mt-0.5">Connect your GitHub to enable real PRs</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  CARTIE requests <code className="text-amber-200">repo, user</code> scopes. Token is encrypted server-side. Detect-only (T4) works without this.
                </div>
              </div>
              <button
                type="button"
                onClick={connectGithub}
                disabled={!currentUserId}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-bold text-xs transition-colors"
                data-testid="gh-connect-btn"
              >
                <Lock className="w-3.5 h-3.5" /> Connect GitHub
              </button>
            </div>
          )}
        </div>

        {/* Tier reference strip */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tier-reference">
          {tiers.map((t) => {
            const meta = TIER_META[t.id] || TIER_META.T3;
            const Icon = meta.icon;
            const isDefault = !!t.default;
            return (
              <div key={t.id} className={`rounded-xl border p-4 ${isDefault ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/10 bg-slate-900/40'}`} data-testid={`tier-card-${t.id}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 text-${meta.color}-400`} />
                  <span className="text-xs font-bold text-white">{t.id} · {t.name}</span>
                  {isDefault && <span className="ml-auto px-1.5 py-0.5 rounded bg-emerald-500/15 text-[9px] font-mono uppercase tracking-widest text-emerald-300">default</span>}
                </div>
                <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">{t.description}</div>
              </div>
            );
          })}
        </div>

        {/* Policies list */}
        <div className="mt-8" data-testid="policies-list">
          {loading ? (
            <div className="text-sm text-slate-500 p-6 text-center">Loading…</div>
          ) : policies.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 p-10 text-center" data-testid="policies-empty">
              <Shield className="w-8 h-8 mx-auto text-emerald-400 mb-3" />
              <p className="text-sm text-white font-bold">No policies yet.</p>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                Repos with no policy fall back to <strong className="text-emerald-300">T3 + Shadow</strong>.
                You can lock that in explicitly or pick a different tier per repo.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {policies.map((p) => (
                <PolicyRow
                  key={p.id} p={p}
                  busy={savingId === p.id}
                  onEdit={() => setEditing(p)}
                  onDelete={() => del(p.id)}
                  onGraduate={() => graduate(p.id)}
                  testid={`policy-${p.id}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Editor modal */}
        {editing && (
          <PolicyEditor
            initial={editing}
            onCancel={() => setEditing(null)}
            onSave={save}
            saving={savingId === (editing.id || 'new')}
          />
        )}
      </section>
    </div>
  );
}

function PolicyRow({ p, busy, onEdit, onDelete, onGraduate, testid }) {
  const meta = TIER_META[p.tier] || TIER_META.T3;
  const Icon = meta.icon;
  const ringColor = {
    rose: 'border-rose-500/30 bg-rose-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    slate: 'border-slate-500/20 bg-slate-500/5',
  }[meta.color] || 'border-white/10 bg-slate-900/40';
  return (
    <div className={`rounded-xl border ${ringColor} p-4`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Icon className={`w-5 h-5 text-${meta.color}-400 shrink-0`} />
          <div className="min-w-0">
            <div className="text-sm font-bold text-white truncate" data-testid={`${testid}-repo`}>{p.repo_full_name}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {p.tier} · {meta.label}
              {p.shadow_mode_enabled && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-300 font-mono" data-testid={`${testid}-shadow-on`}>
                  <EyeOff className="w-3 h-3" /> shadow ON
                </span>
              )}
              {!p.shadow_mode_enabled && (
                <span className="ml-2 inline-flex items-center gap-1 text-emerald-300 font-mono" data-testid={`${testid}-shadow-off`}>
                  <Eye className="w-3 h-3" /> live
                </span>
              )}
            </div>
            {p.excluded_paths?.length > 0 && (
              <div className="text-[10px] text-slate-500 mt-1 font-mono flex items-center gap-1">
                <Lock className="w-3 h-3" /> excluded: {p.excluded_paths.join(', ')}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-1.5">
          {p.shadow_mode_enabled && (
            <button
              type="button"
              disabled={busy}
              onClick={onGraduate}
              className="px-2.5 py-1.5 rounded border border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/10 text-emerald-300 text-[11px] font-bold transition-colors disabled:opacity-50"
              data-testid={`${testid}-graduate-btn`}
            >
              Graduate from shadow
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={onEdit}
            className="p-1.5 rounded border border-white/10 hover:border-emerald-500/40 hover:text-emerald-300 transition-colors disabled:opacity-50"
            data-testid={`${testid}-edit-btn`}
            aria-label="Edit"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            className="p-1.5 rounded border border-white/10 hover:border-rose-400/40 hover:text-rose-400 transition-colors disabled:opacity-50"
            data-testid={`${testid}-delete-btn`}
            aria-label="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PolicyEditor({ initial, onCancel, onSave, saving }) {
  const [form, setForm] = useState({
    id: initial.id || null,
    repo_full_name: initial.repo_full_name || '',
    tier: initial.tier || 'T3',
    shadow_mode_enabled: initial.shadow_mode_enabled !== false,
    excluded_paths_str: (initial.excluded_paths || []).join('\n'),
    reviewer_team: initial.reviewer_team || '',
    notes: initial.notes || '',
  });

  const submit = (e) => {
    e.preventDefault();
    onSave({
      id: form.id,
      repo_full_name: form.repo_full_name.trim(),
      tier: form.tier,
      shadow_mode_enabled: form.shadow_mode_enabled,
      excluded_paths: form.excluded_paths_str.split('\n').map((s) => s.trim()).filter(Boolean),
      reviewer_team: form.reviewer_team || null,
      notes: form.notes || null,
    });
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="policy-editor-modal">
      <form onSubmit={submit} className="w-full max-w-lg bg-slate-950 border border-white/10 rounded-2xl p-6 my-8" data-testid="policy-editor-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">{form.id ? 'Edit policy' : 'New automation policy'}</h2>
          <button type="button" onClick={onCancel} className="text-slate-500 hover:text-white" data-testid="policy-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block mb-3">
          <div className="text-xs text-slate-400 mb-1">Repo (owner/name)</div>
          <input
            required
            disabled={!!form.id}
            value={form.repo_full_name}
            onChange={(e) => setForm({ ...form, repo_full_name: e.target.value })}
            placeholder="acme-corp/payments-api"
            className="w-full bg-slate-900 border border-white/10 rounded px-3 py-2 text-sm font-mono text-slate-200 focus:outline-none focus:border-emerald-500/40 disabled:opacity-50"
            data-testid="policy-repo-input"
          />
        </label>

        <div className="mb-3">
          <div className="text-xs text-slate-400 mb-1">Tier</div>
          <div className="grid grid-cols-2 gap-2" data-testid="tier-radio-group">
            {['T1', 'T2', 'T3', 'T4'].map((t) => {
              const meta = TIER_META[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, tier: t })}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                    form.tier === t
                      ? `border-${meta.color}-500/50 bg-${meta.color}-500/10`
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`tier-radio-${t}`}
                >
                  <div className="flex items-center gap-1.5 font-bold text-white">{t} · {meta.label}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{meta.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.shadow_mode_enabled}
            onChange={(e) => setForm({ ...form, shadow_mode_enabled: e.target.checked })}
            className="accent-emerald-400 w-4 h-4"
            data-testid="shadow-mode-checkbox"
          />
          <span className="text-sm text-slate-200">
            Shadow mode <span className="text-xs text-slate-400">(generate diffs but DON&apos;T open PRs for 14 days)</span>
          </span>
        </label>

        <label className="block mb-3">
          <div className="text-xs text-slate-400 mb-1">Excluded paths (one glob per line)</div>
          <textarea
            rows={3}
            value={form.excluded_paths_str}
            onChange={(e) => setForm({ ...form, excluded_paths_str: e.target.value })}
            placeholder="src/payments/**&#10;**/*.secret"
            className="w-full bg-slate-900 border border-white/10 rounded px-3 py-2 text-xs font-mono text-slate-200 focus:outline-none focus:border-emerald-500/40"
            data-testid="excluded-paths-input"
          />
        </label>

        <label className="block mb-3">
          <div className="text-xs text-slate-400 mb-1">Reviewer team (GitHub team slug, optional)</div>
          <input
            value={form.reviewer_team}
            onChange={(e) => setForm({ ...form, reviewer_team: e.target.value })}
            placeholder="acme-corp/finops-reviewers"
            className="w-full bg-slate-900 border border-white/10 rounded px-3 py-2 text-sm font-mono text-slate-200 focus:outline-none focus:border-emerald-500/40"
            data-testid="reviewer-team-input"
          />
        </label>

        <label className="block mb-4">
          <div className="text-xs text-slate-400 mb-1">Notes (visible to your team only)</div>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full bg-slate-900 border border-white/10 rounded px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-emerald-500/40"
            data-testid="policy-notes-input"
          />
        </label>

        {form.tier === 'T1' && (
          <div className="mb-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 px-3 py-2 text-xs flex gap-2" data-testid="t1-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>T1 will auto-merge CARTIE PRs after CI passes + 24h soak. Only enable on repos where you trust our diffs implicitly.</span>
          </div>
        )}

        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-xs text-slate-400 hover:text-white transition-colors" data-testid="policy-cancel-btn">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm disabled:opacity-50 transition" data-testid="policy-save-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save policy'}
          </button>
        </div>
      </form>
    </div>
  );
}
